import { IconInbox, IconFilterOff } from '@tabler/icons-react';
import type { ComponentPropsWithRef, ReactNode } from 'react';

import { cn } from '../../lib/cn';
import { Button } from '../core/Button';
import { useTranslation } from '../../i18n/LocaleProvider';

export type EmptyStateVariant = 'page' | 'table' | 'kpi' | 'cards';

/** The four placements differ only in scale — `page` is the largest. */
const LAYOUT: Record<
  EmptyStateVariant,
  { root: string; icon: string; title: string; description: string }
> = {
  page: { root: 'gap-5 px-8 py-20', icon: 'size-14', title: 'text-2xl', description: 'text-base' },
  table: { root: 'gap-4 px-6 py-14', icon: 'size-10', title: 'text-lg', description: 'text-sm' },
  kpi: { root: 'gap-3 px-4 py-8', icon: 'size-8', title: 'text-sm', description: 'text-xs' },
  cards: { root: 'gap-4 px-6 py-12', icon: 'size-10', title: 'text-lg', description: 'text-sm' },
};

export interface EmptyStateProps extends Omit<ComponentPropsWithRef<'div'>, 'title' | 'children'> {
  variant?: EmptyStateVariant;
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
  /** Table variant: the action offered when the empty result is filter-caused. */
  onClearFilters?: () => void;
  /** True only when the user's filters are the reason the dataset is empty. */
  filtersActive?: boolean;
}

/**
 * The shared "nothing here" surface. It is static by contract: an empty/error
 * state is on the list of components with no natural animated state, so it
 * never carries an entrance — the state change is announced instead
 * (`role="alert"` on the error, the text swap here).
 *
 * The clear-filters action is gated on `filtersActive` by design: a genuinely
 * empty dataset must not offer to undo filters that are not the cause, and an
 * `onClearFilters` with no active filters renders nothing rather than a dead
 * button.
 */
export function EmptyState({
  variant = 'page',
  icon,
  title,
  description,
  action,
  onClearFilters,
  filtersActive = false,
  className,
  ...rest
}: EmptyStateProps) {
  const layout = LAYOUT[variant];
  const { t } = useTranslation();
  const showClearFilters = filtersActive === true && typeof onClearFilters === 'function';

  return (
    <div
      {...rest}
      className={cn(
        'flex w-full flex-col items-center justify-center text-center',
        layout.root,
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'inline-grid shrink-0 place-items-center text-muted [&>svg]:size-full',
          layout.icon,
        )}
      >
        {icon ?? <IconInbox />}
      </span>

      <p className={cn('font-display font-semibold uppercase tracking-title text-heading', layout.title)}>
        {title}
      </p>

      {description ? (
        <p className={cn('max-w-prose text-muted', layout.description)}>{description}</p>
      ) : null}

      {action || showClearFilters ? (
        <div className="mt-1 flex flex-wrap items-center justify-center gap-3">
          {action}
          {showClearFilters ? (
            <Button variant="outline" size="sm" leadingIcon={<IconFilterOff />} onClick={onClearFilters}>
              {t('ds.empty.clearFilters')}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
